import React from 'react';
import * as R from 'ramda';
import { Box, Button, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { DrawerLayoutWrapper } from 'common';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { createSnack } from 'snackbar/snackbarSlice';
import { formatNumberWithCommas } from 'utils/helpers/format';
import { useCreateAutoReconsignmentMutation } from '../autoReconsignmentsEndpoints';

const AutoSummaryStep = ({ prev, next, onClose, reconsignmentState }) => {
  const dispatch = useDispatch();

  const formatOrigins = origins => {
    if (R.or(R.isNil(origins), R.isEmpty(origins))) return 'ANY';
    return origins.map(origin => {
      return `${origin.originName}`;
    });
  };

  const {
    inventoryOwner,
    product,
    fromLocation,
    toLocation,
    thresholdVolume,
    transferVolume,
    origins,
  } = reconsignmentState;

  const body = {
    fromLocationIds: [R.path(['locationId'], fromLocation)],
    inventoryOwner: {
      companyCustomerIdName: R.path(['customerIdName'], inventoryOwner),
      dataCompanyId: R.path(['dataCompanyId'], inventoryOwner),
    },
    originIds: R.map(R.prop('originId'), origins ?? []),
    productId: R.path(['productId'], product),
    thresholdVolume: Number(thresholdVolume),
    toLocationIds: [R.path(['locationId'], toLocation)],
    volume: Number(transferVolume),
  };

  const [autoSubmit, { isLoading, isSuccess }] = useCreateAutoReconsignmentMutation();

  const handleSubmit = () => {
    autoSubmit({ body }).then(res => {
      if (res.error) {
        dispatch(
          createSnack({
            message: res.error.data?.title,
            options: { variant: 'error' },
          })
        );
      } else {
        next();
      }
    });
  };

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: 1 }}>
      <Button onClick={prev} sx={{ width: 150 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isSuccess || isLoading} onClick={handleSubmit} sx={{ width: 150 }}>
        {isLoading ? <CircularProgress size={24} /> : 'Submit'}
      </Button>
    </Box>
  );
  return (
    <DrawerLayoutWrapper
      buttons={actionButtons}
      headerButtonText="CLOSE"
      onHeaderButtonClick={onClose}
      title="New Auto Reconsignment"
    >
      <Box
        sx={{
          borderBottom: 1,
          borderBottomColor: 'graphic.light',
          p: 3,
          width: '100%',
        }}
      >
        <LinearProgress data-variant="magellanProgressBar" value={100} variant="determinate" />
        <Typography>Summary (2 of 2)</Typography>
      </Box>
      <Stack>
        <Typography sx={{ p: 3 }} variant="body1">
          Please review the summary below to ensure all information is correct.
        </Typography>

        <TitleBox label="1. Details" />

        <ItemBox
          label="Affiliate"
          value={R.path(['inventoryOwner', 'customerNameShort'], reconsignmentState)}
        />
        <ItemBox label="Product" value={R.path(['product', 'productName'], reconsignmentState)} />
        <ItemBox
          label="From Location"
          value={R.path(['fromLocation', 'locationName'], reconsignmentState)}
        />
        <ItemBox
          label="To Location"
          value={R.path(['toLocation', 'locationName'], reconsignmentState)}
        />
        <ItemBox
          label="Threshold Volume"
          value={`${formatNumberWithCommas(R.prop('thresholdVolume', reconsignmentState))} bbl`}
        />
        <ItemBox
          label="Transfer Volume"
          value={`${formatNumberWithCommas(R.prop('transferVolume', reconsignmentState))} bbl`}
        />
        {!R.either(R.isEmpty, R.isNil)(R.prop('origins', reconsignmentState)) && (
          <Box mt={2}>
            <TitleBox label="2. Origin(s)" />
          </Box>
        )}
        <ItemBox label="Origins" value={formatOrigins(R.prop('origins', reconsignmentState))} />
      </Stack>
    </DrawerLayoutWrapper>
  );
};

const TitleBox = ({ label }) => {
  return (
    <Box bgcolor="graphic.light" mb={1} px={3} py={1} typography="subtitle1">
      {label}
    </Box>
  );
};
const ItemBox = ({ label, value }) => {
  return (
    <Stack alignItems="center" direction="row" justifyContent="space-between" px={2} py={1}>
      <Typography color="text.primary" noWrap sx={{ minWidth: 140 }} variant="body1">
        {label}
      </Typography>
      {Array.isArray(value) ? (
        <Stack direction="column" sx={{ maxWidth: 220 }}>
          {value.map(item => (
            <Typography
              color="text.primary"
              key={item}
              noWrap
              overflow="hidden"
              p={0.5}
              textAlign="left"
              textOverflow="ellipsis"
              variant="body1"
            >
              {item}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography color="text.primary" textAlign="right" variant="body1">
          {value}
        </Typography>
      )}
    </Stack>
  );
};

AutoSummaryStep.propTypes = {
  next: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  prev: PropTypes.func.isRequired,
  reconsignmentState: PropTypes.shape({
    fromLocation: PropTypes.shape({}),
    inventoryOwner: PropTypes.shape({}),
    origins: PropTypes.arrayOf(PropTypes.shape({})),
    product: PropTypes.shape({}),
    thresholdVolume: PropTypes.number,
    toLocation: PropTypes.shape({}),
    transferVolume: PropTypes.number,
  }).isRequired,
};

TitleBox.propTypes = {
  label: PropTypes.string.isRequired,
};

ItemBox.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]).isRequired,
};

export default AutoSummaryStep;
